/////////////////////////////////////////////
//Dependencies
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";

/////////////////////////////////////////////
//CSS
import './cookiepopup.css';

/////////////////////////////////////////////
//Component

/**
 * CookiePopup component
 * 
 * @returns {JSX.Element|null} - The rendered CookiePopup component or null if the popup should not be shown.
 */
const GA_ID = 'G-KLS816899F';
const CLARITY_ID = 'mq5ijmgbh6';

export default function CookiePopup() {
    const { t } = useTranslation();
    const [showPopup, setShowPopup] = useState(false);
    const [accepted, setAccepted] = useState(false);

    function checkCookie(name) {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        if (match) return match[2];
        else return null;
    }

    function loadScript(scriptContent) {
        const script = document.createElement('script');
        script.textContent = scriptContent;
        document.head.appendChild(script);
    }

    function loadExternalScript(src) {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        document.head.appendChild(script);
    }

    /**
     * Load Google Analytics + Microsoft Clarity.
     * Guarded so it can run safely on every page load when consent is already given.
     */
    function loadAnalytics() {
        if (window.__plassysAnalyticsLoaded) return;
        window.__plassysAnalyticsLoaded = true;

        // Google Analytics
        loadExternalScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', GA_ID);

        // Microsoft Clarity
        loadScript(`
            (function (c, l, a, r, i, t, y) {
                c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
                t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
                y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
            })(window, document, "clarity", "script", "${CLARITY_ID}");
        `);
    }

    useEffect(() => {
        const consent = checkCookie('cookie-consent');
        if (!consent) {
            setShowPopup(true);
        } else if (consent === 'accepted') {
            setAccepted(true);
            loadAnalytics();
        }
    }, []);

    const handleRefuse = () => {
        document.cookie = "cookie-consent=refused; max-age=15768000 ; path=/"; //Cookie expires in 6 months (15768000sec) (GDPR)
        setShowPopup(false);
        setAccepted(false);
    };

    const handleAccept = () => {
        document.cookie = "cookie-consent=accepted; max-age=15768000 ; path=/"; //Cookie expires in 6 months (15768000sec) (GDPR)
        setShowPopup(false);
        setAccepted(true);
        loadAnalytics();
    };

    if (!showPopup) {
        return null;
    }

    return (
        <div className="cookie-popup">
            <div>
                <p>{t('popupcookie.text1')}</p>
                <p>{t('popupcookie.text2')} <Link to="/privacypolicy">{t('popupcookie.link')}</Link>.</p>
            </div>
            <div className='button-container-popup'>
                <button className="btn-reject" onClick={handleRefuse}>{t('popupcookie.button2')}</button>
                <button className="btn-accept" onClick={handleAccept}>{t('popupcookie.button')}</button>
            </div>
        </div>
    );
};